/**
 * The example payloads the API documentation shows, built from a real table.
 *
 * Every example on the page is generated from the reader's own fields, so the
 * keys are their column names or field ids and the values are of the right
 * shape for each type. That is the point of the page: a generic example of a
 * row is what the OpenAPI contract already gives, and it is not enough to write
 * a request against.
 */
import type { Field } from '@/api/types'
import { READ_ONLY_REQUEST, docsFor } from '../fields/docs'

export interface ExampleOptions {
  /**
   * Whether the keys are column names or `field_<id>`.
   *
   * Both are real: `user_field_names=true` is friendlier to write and breaks
   * when a column is renamed, and the ids never change. The page offers the
   * switch because the choice belongs to whoever is writing the request.
   */
  userFieldNames: boolean
}

/** The key a field is addressed by in a row payload. */
export function fieldKey(field: Field, options: ExampleOptions): string {
  return options.userFieldNames ? field.name : `field_${field.id}`
}

/**
 * A row as it is sent.
 *
 * Read-only fields are left out entirely rather than shown with a placeholder:
 * the endpoint refuses the key, so including it would be an example of a
 * request that fails.
 */
export function requestExample(
  fields: readonly Field[],
  options: ExampleOptions,
): Record<string, unknown> {
  const item: Record<string, unknown> = {}
  for (const field of fields) {
    if (field.read_only) continue
    item[fieldKey(field, options)] = docsFor(field.type).requestExample(field)
  }
  return item
}

/**
 * A row as it comes back.
 *
 * `id` and `order` come first because they are on every row whatever the table
 * holds, and `order` is a decimal *string*: it is arbitrary precision, and a
 * client that parses it as a number loses the ordering it was sent for.
 */
export function responseExample(
  fields: readonly Field[],
  options: ExampleOptions,
): Record<string, unknown> {
  const item: Record<string, unknown> = { id: 0, order: '1.00000000000000000000' }
  for (const field of fields) {
    item[fieldKey(field, options)] = docsFor(field.type).responseExample(field)
  }
  return item
}

/**
 * A batch payload, which carries `id` inside each item.
 *
 * The single-row endpoints take the id in the path; the batch ones cannot, so
 * every item names the row it changes.
 */
export function batchRequestExample(
  fields: readonly Field[],
  options: ExampleOptions,
): Record<string, unknown> {
  return { items: [{ id: 0, ...requestExample(fields, options) }] }
}

export function batchResponseExample(
  fields: readonly Field[],
  options: ExampleOptions,
): Record<string, unknown> {
  return { items: [responseExample(fields, options)] }
}

export const BATCH_DELETE_EXAMPLE = { items: [0] }

/**
 * The other name for every field, so a reader can translate between the two
 * ways of addressing a row.
 *
 * Keyed by whichever naming is in force and valued by the other, which is the
 * direction somebody reading the page needs: they have the example in front of
 * them and want to know what it would be called the other way.
 */
export function fieldMapping(
  fields: readonly Field[],
  options: ExampleOptions,
): Record<string, string> {
  const mapping: Record<string, string> = {}
  for (const field of fields) {
    if (options.userFieldNames) mapping[field.name] = `field_${field.id}`
    else mapping[`field_${field.id}`] = field.name
  }
  return mapping
}

/** What a read-only field is told when somebody tries to send one. */
export { READ_ONLY_REQUEST }

// ---------------------------------------------------------------------------
// Addresses
// ---------------------------------------------------------------------------

/**
 * Built against the configured backend rather than this page's own origin,
 * because the request being documented is one somebody will make from a server
 * somewhere else -- and on a split deployment those are different addresses.
 */
export function rowsUrl(
  backend: string,
  tableId: number,
  options: { batch?: boolean; userFieldNames?: boolean } = {},
): string {
  const path = `${backend}/api/database/rows/table/${tableId}/${options.batch === true ? 'batch/' : ''}`
  return options.userFieldNames === true ? `${path}?user_field_names=true` : path
}

export function rowUrl(
  backend: string,
  tableId: number,
  options: { userFieldNames?: boolean } = {},
): string {
  const path = `${backend}/api/database/rows/table/${tableId}/{row_id}/`
  return options.userFieldNames === true ? `${path}?user_field_names=true` : path
}

export function batchDeleteUrl(backend: string, tableId: number): string {
  return `${backend}/api/database/rows/table/${tableId}/batch-delete/`
}

export function fieldsUrl(backend: string, tableId: number): string {
  return `${backend}/api/database/fields/table/${tableId}/`
}

export function uploadFileUrl(backend: string): string {
  return `${backend}/api/user-files/upload-file/`
}

export function uploadViaUrlUrl(backend: string): string {
  return `${backend}/api/user-files/upload-via-url/`
}

export function tablesUrl(backend: string, databaseId: number): string {
  return `${backend}/api/database/tables/database/${databaseId}/`
}
